import { QuestionProps } from '.';
import { BottomSpacer, SplitScreenQuestion, getQuestionText } from './common';
import { ReadableContainer, ReadableMarkdown } from '../Containers';
import { H1 } from '../../elements/Text';
import React, { useCallback } from 'react';
import {
  AIWorkflow,
  MESSAGE_CONTEXT_TYPES,
} from '../../../contexts/ai-workflow';
import {
  ACTIVITY_ACTION,
  ActivityContext,
  ActivityContextLLMPlaygroundQuestionAnswer,
} from '../../../contexts/activity';
import { ACTIVITY_STEP } from '../Content';
import MentorReview from '../MentorComment';
import LLMPlayground from '../../LLMPlayground';
import { usePreviousDistinct, useUpdateEffect } from 'react-use';
import { toast } from 'react-toastify';
import IncorrectAnswerToast from '../../Toasts/TryAgain';
import { useLLMPlaygroundValidationPoller } from '../../../hooks/use-activity-question-validation-polling';
import { useAnswerStreamSubject } from '../../../answer-stream/context';
import { useLLMPlaygroundStream } from '../../../answer-stream/use-llm-playground-stream';

export default function LLMPlaygroundQuestion({
  question,
  answer,
  getActivity,
  submitAnswer,
}: QuestionProps & { answer: ActivityContextLLMPlaygroundQuestionAnswer }) {
  const { setMessageContext } = React.useContext(AIWorkflow);
  const [activity, dispatch] = React.useContext(ActivityContext);
  const answerValue = answer.value;

  const prevAnswer = usePreviousDistinct(answer);
  const qIndex =
    activity.currentStep?.type === ACTIVITY_STEP.QUESTION
      ? activity?.currentStep?.questionIndex
      : -1;

  const mentorComment =
    activity.latestSavedSubmission?.mentor_reviews?.find(
      (review) => review.questionId === question.id
    )?.review ?? null;

  const [isLoading, setIsLoading] = React.useState(false);
  const answerStreamSubject = useAnswerStreamSubject();

  const onPromptChange = React.useCallback(
    (prompt: string) => {
      dispatch({
        type: ACTIVITY_ACTION.CHANGE_VALUE_QUESTION_LLM_PLAYGROUND,
        payload: {
          question,
          value: prompt,
        },
      });
    },
    [dispatch, question]
  );

  const {
    pollActivity,
    isPolling,
    error: validationError,
  } =
    useLLMPlaygroundValidationPoller({
      getActivity,
      dispatch,
    });

  const submitStreamAnswer = React.useCallback(
    () =>
      submitAnswer({
        value: answerValue,
        questionSlug: question.slug,
      }),
    [submitAnswer, answerValue, question.slug]
  );

  const answerStream = useLLMPlaygroundStream({
    subject: answerStreamSubject,
    questionSlug: question.slug,
    persistedOutput: (answer.result as string | null) ?? null,
    persistedReview: (answer.review as string | null) ?? null,
    persistedIsCorrect: answer.isCorrect,
    submitAnswer: submitStreamAnswer,
  });

  const onSubmitPrompt = React.useCallback(async () => {
    if (isLoading || answerStream.isBusy) {
      return;
    }
    dispatch({
      type: ACTIVITY_ACTION.CLEAR_RESULT_AND_REVIEW,
      payload: {
        question,
      },
    });

    if (answerStreamSubject) {
      await answerStream.submit();
      void pollActivity(answer.questionSlug);
      return;
    }

    setIsLoading(true);

    await submitAnswer({
      value: answerValue,
      questionSlug: question.slug,
    });

    await pollActivity(answer.questionSlug);
    setIsLoading(false);
  }, [
    dispatch,
    setIsLoading,
    question,
    pollActivity,
    answer,
    answerValue,
    submitAnswer,
    isLoading,
    answerStream,
    answerStreamSubject,
  ]);

  const onHint = React.useCallback(() => {
    setMessageContext({
      context_type: MESSAGE_CONTEXT_TYPES.QUESTION_HINT,
      context_content: {
        question_index: qIndex,
        attempted_answer: answer.value,
        is_correct: answer.isCorrect,
        result: answer.result as string | null,
      },
    });
  }, [answer, qIndex, setMessageContext]);

  useUpdateEffect(() => {
    if (answer.questionSlug === prevAnswer?.questionSlug) {
      if (answer.isCorrect) {
        toast.success(
          answer.doesSatisfyAllRequirements === true
            ? 'Great job!'
            : 'Pretty good! See review for suggestions.'
        );
      } else if (answer.isCorrect === false) {
        toast.warn(() => <IncorrectAnswerToast onHint={onHint} />);
      }
    }
  }, [answer.isCorrect]);

  const onReset = useCallback(() => {
    const shouldReset = confirm(
      "Are you sure? This will clear everything you've done in this exercise. Hit Cancel to undo! Or OK to go ahead and reset everything."
    );
    if (!shouldReset) {
      return;
    }
    dispatch({
      type: ACTIVITY_ACTION.CLEAR_RESULT_AND_REVIEW,
      payload: {
        question,
      },
    });
    dispatch({
      type: ACTIVITY_ACTION.CHANGE_VALUE_QUESTION_LLM_PLAYGROUND,
      payload: {
        question,
        value: question.llm_initial_prompt ?? '',
      },
    });
    answerStream.reset();
  }, [dispatch, question, answerStream]);

  return (
    <SplitScreenQuestion>
      <>
        <ReadableContainer>
          <H1>Exercise {qIndex + 1}</H1>
          {!!mentorComment && <MentorReview comment={mentorComment} />}
        </ReadableContainer>
        <ReadableMarkdown md={getQuestionText(question)} />
        <BottomSpacer />
      </>
      <>
        <LLMPlayground
          userPrompt={answerValue}
          onPromptChange={onPromptChange}
          result={
            answerStreamSubject
              ? answerStream.output
              : (answer.result as string | null)
          }
          review={
            answerStreamSubject
              ? answerStream.review
              : (answer.review as string | null)
          }
          onSubmitPrompt={onSubmitPrompt}
          onReset={onReset}
          isLoading={isLoading || answerStream.isBusy || isPolling}
          validationError={
            answerStreamSubject
              ? answerStream.error ??
                (answerStream.status === 'awaiting' ? validationError : null)
              : validationError
          }
        />
      </>
    </SplitScreenQuestion>
  );
}
